import { useCallback, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { functions as functionsClient } from '../firebase';
import { AppView, ListUsersResponse, ManagedUser } from '../types/app';

export function useManagedUsers(currentUser: User | null, isAdmin: boolean, view: AppView) {
  const [managedUsers, setManagedUsers] = useState<ManagedUser[]>([]);
  const [isLoadingManagedUsers, setIsLoadingManagedUsers] = useState(false);
  const [adminError, setAdminError] = useState('');
  const [adminSuccess, setAdminSuccess] = useState('');

  const fetchManagedUsers = useCallback(async () => {
    setAdminError('');
    setAdminSuccess('');
    setIsLoadingManagedUsers(true);

    try {
      const listUsers = httpsCallable<unknown, ListUsersResponse>(
        functionsClient,
        'listUsersForAdmin'
      );
      const response = await listUsers({});
      setManagedUsers(response.data.users || []);
    } catch (error: unknown) {
      setAdminError('Kunne ikke hente brugere. Bekraeft adminadgang og deployede funktioner.');
    } finally {
      setIsLoadingManagedUsers(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setManagedUsers([]);
      return;
    }

    if (!isAdmin || view !== 'admin') {
      return;
    }

    void fetchManagedUsers();
  }, [currentUser, isAdmin, view, fetchManagedUsers]);

  const handleSetWhitelist = async (uid: string, whitelisted: boolean) => {
    setAdminError('');
    setAdminSuccess('');

    try {
      const setWhitelist = httpsCallable(functionsClient, 'setUserWhitelist');
      await setWhitelist({uid, whitelisted});
      setManagedUsers((previous) =>
        previous.map((userEntry) =>
          userEntry.uid === uid ? {...userEntry, whitelisted} : userEntry
        )
      );
      setAdminSuccess('Whitelist er opdateret.');
    } catch (error: unknown) {
      setAdminError('Kunne ikke opdatere whitelist-status.');
    }
  };

  const handleSetUserMemberLink = async (uid: string, linkedMemberId: string | null) => {
    setAdminError('');
    setAdminSuccess('');

    try {
      const setMemberLink = httpsCallable(functionsClient, 'setUserMemberLink');
      await setMemberLink({uid, linkedMemberId});
      setManagedUsers((previous) =>
        previous.map((userEntry) =>
          userEntry.uid === uid ? {...userEntry, linkedMemberId} : userEntry
        )
      );
      setAdminSuccess(linkedMemberId ? 'Medlem tilknyttet.' : 'Tilknytning fjernet.');
    } catch (error: unknown) {
      setAdminError('Kunne ikke opdatere medlemstilknytning.');
    }
  };

  const handleDeleteUser = async (uid: string, emailValue: string) => {
    const confirmation = window.confirm(
      `Slet ${emailValue || uid}? Dette sletter baade auth-konto og profil.`
    );
    if (!confirmation) {
      return;
    }

    setAdminError('');
    setAdminSuccess('');
    try {
      const deleteUser = httpsCallable(functionsClient, 'deleteUserByAdmin');
      await deleteUser({uid});
      setManagedUsers((previous) => previous.filter((userEntry) => userEntry.uid !== uid));
      setAdminSuccess('Bruger er slettet.');
    } catch (error: unknown) {
      setAdminError('Kunne ikke slette bruger.');
    }
  };

  return {
    managedUsers,
    isLoadingManagedUsers,
    adminError,
    adminSuccess,
    fetchManagedUsers,
    handleSetWhitelist,
    handleSetUserMemberLink,
    handleDeleteUser,
  };
}
